/* eslint-disable no-param-reassign */
import { useEffect, useRef } from 'react';
import {
  $getRoot,
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  LexicalNode,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { InlineBoxType } from '$/components/InlineBox/types';
import {
  $isInlineBoxNode,
} from './nodes';
import { collectNodes } from './pluginUtils';
import { syncInlineBoxSelectedKeys } from './inlineBoxStateStore';

/** 比较两个 Set，避免相同 NodeSelection 重复刷新运行时选中态。 */
function isSameSet<T>(left: Set<T>, right: Set<T>): boolean {
  if (left.size !== right.size) return false;
  for (const item of left) {
    if (!right.has(item)) return false;
  }
  return true;
}

/** 同步 Lexical 选区到 InlineBox 外置选中态，并按 Mark 业务 id 对外通知。 */
export function SelectionSyncPlugin({ onSelect }: { onSelect?: (params: { id?: string; }) => void; }) {
  const [editor] = useLexicalComposerContext();
  const lastSelectedNodeKeysRef = useRef<Set<string>>(new Set());
  const lastActiveMarkRef = useRef<string | null>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    return editor.registerCommand(SELECTION_CHANGE_COMMAND, () => {
      // 选中的 lexical nodeKey 集合（决定 InlineBox 视觉态）
      const nextSelectedNodeKeys = new Set<string>();
      let nextSelectedMarkId: string | null = null;
      const selection = $getSelection();
      if (!selection) return false;

      if ($isNodeSelection(selection)) {
        const nodes = selection.getNodes();
        nodes.forEach((node) => {
          if ($isInlineBoxNode(node)) {
            nextSelectedNodeKeys.add(node.getKey());
            if (!nextSelectedMarkId && node.__inlineBox?.type === InlineBoxType.Mark) {
              nextSelectedMarkId = node.getInlineId();
            }
          }
        });
      } else if ($isRangeSelection(selection) && !selection.isCollapsed()) {
        const domSelection = window.getSelection();
        const domRange = domSelection && domSelection.rangeCount > 0 ? domSelection.getRangeAt(0) : null;
        const rootElement = editor.getRootElement();
        if (!domRange || !rootElement) return false;
        if (!rootElement.contains(domSelection?.anchorNode || null)) return false;

        const root = $getRoot();
        const nodes: LexicalNode[] = [];
        root.getChildren().forEach((child) => collectNodes(child, nodes));
        nodes.forEach((node) => {
          if ($isInlineBoxNode(node)) {
            const element = editor.getElementByKey(node.getKey());
            if (element && domRange.intersectsNode(element)) {
              nextSelectedNodeKeys.add(node.getKey());
              if (!nextSelectedMarkId && node.__inlineBox?.type === InlineBoxType.Mark) {
                nextSelectedMarkId = node.getInlineId();
              }
            }
          }
        });
      }

      const selectedNodeKeysChanged = !isSameSet(nextSelectedNodeKeys, lastSelectedNodeKeysRef.current);
      if (selectedNodeKeysChanged) {
        lastSelectedNodeKeysRef.current = nextSelectedNodeKeys;
        // 把选中态写到外置 store；不调 getWritable，避免改 EditorState 导致 history push。
        syncInlineBoxSelectedKeys(editor, nextSelectedNodeKeys);
      }

      if (nextSelectedMarkId) {
        if (nextSelectedMarkId !== lastActiveMarkRef.current) {
          lastActiveMarkRef.current = nextSelectedMarkId;
          onSelectRef.current?.({ id: nextSelectedMarkId });
        }
      } else if (lastActiveMarkRef.current) {
        lastActiveMarkRef.current = null;
        onSelectRef.current?.({});
      }

      return false;
    }, COMMAND_PRIORITY_LOW);
  }, [editor]);

  return null;
}
